import { useState } from 'react';
import { Box, Card, CardContent, Stack, Typography, Button, Avatar } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/StorefrontOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useRequiredBusiness } from '@/hooks/useRequiredBusiness';
import { useBusinessStore } from '@/store/businessStore';
import CreateBusinessDialog from '@/components/CreateBusinessDialog';
import { SkeletonRows } from '@/components/LoadingState';
import { initialsOf, projectColor, PALETTE } from '@/theme/tokens';

/**
 * Wraps pages that cannot operate on "All Projects". Renders `children` only
 * once a real business is selected; otherwise asks the user to pick or create
 * one. A single-business account is selected automatically.
 */
export default function RequireBusiness({ children, title = 'Choose a business' }) {
  const { ready, isLoading, needsChoice, needsBusiness, businesses } = useRequiredBusiness();
  const setCurrentBusiness = useBusinessStore((s) => s.setCurrentBusiness);
  const [createOpen, setCreateOpen] = useState(false);

  if (isLoading) return <SkeletonRows rows={4} />;
  if (ready) return children;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 4 }}>
      <Card sx={{ borderRadius: 3.5, border: `1px solid ${PALETTE.divider}` }}>
        <CardContent sx={{ p: 3.5, textAlign: 'center' }}>
          <Box
            sx={{
              width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'primary.main', color: '#fff',
            }}
          >
            <StorefrontIcon sx={{ fontSize: 28 }} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            {needsBusiness ? 'Create your first business' : title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {needsBusiness
              ? 'This page works on a single business. Add one to get started.'
              : 'This page works on one business at a time — “All Projects” can’t be used here. Pick which business you’re working on.'}
          </Typography>

          {needsChoice && (
            <Stack spacing={1} sx={{ mb: 2.5 }}>
              {businesses.map((b) => (
                <Stack
                  key={b.id}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  onClick={() => setCurrentBusiness(b.id)}
                  sx={{
                    p: 1.25, borderRadius: 2.5, cursor: 'pointer', textAlign: 'left',
                    border: `1px solid ${PALETTE.divider}`,
                    transition: 'all .15s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                  }}
                >
                  <Avatar
                    src={b.logo || undefined}
                    sx={{ width: 36, height: 36, bgcolor: b.profile?.color || projectColor(b.id || b.name), fontWeight: 700 }}
                  >
                    {initialsOf(b.name)}
                  </Avatar>
                  <Typography sx={{ fontWeight: 700, flex: 1 }} noWrap>{b.name}</Typography>
                </Stack>
              ))}
            </Stack>
          )}

          <Button
            variant={needsBusiness ? 'contained' : 'outlined'}
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            disableElevation
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
          >
            Add new business
          </Button>
        </CardContent>
      </Card>

      <CreateBusinessDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </Box>
  );
}
